/* global initLazyImages */

if (!customElements.get('shirt-finder-quiz')) {
  class ShirtFinderQuiz extends HTMLElement {
    constructor() {
      super();
      this.state = {
        step: 0,
        kids: false,
        size: null,
        picks: {},
        facets: null,
        sizeFilterParam: null,
        sizeAvailableValues: [],
        resultsHtml: null,
        loading: false,
        error: null,
        viewingValues: false,
        searchQuery: '',
        history: []
      };
      this.pendingAnalyticsPayloads = [];
      this.analyticsRetryTimer = null;
      this.analyticsRetryAttempts = 0;
    }

    connectedCallback() {
      this.baseCollectionUrl = this.dataset.baseCollectionUrl || '/collections/all';
      this.sectionId = this.dataset.sectionId || 'collection-products';
      this.drillAxes = this.splitList(this.dataset.drillAxes);
      this.optionalFromIndex = parseInt(this.dataset.optionalFromIndex || '2', 10);
      this.autoSkipThreshold = parseInt(this.dataset.autoSkipThreshold || '2', 10);
      this.searchThreshold = parseInt(this.dataset.searchThreshold || '10', 10);
      this.sizeFilterLabel = (this.dataset.sizeFilterLabel || 'Maat').trim();
      this.adultSizes = this.splitList(this.dataset.adultSizes);
      this.kidsSizes = this.splitList(this.dataset.kidsSizes);
      this.kidsToggleEnabled = this.dataset.kidsToggleEnabled === 'true';
      this.resultsLimit = parseInt(this.dataset.resultsLimit || '12', 10);
      this.ga4MeasurementId = (this.dataset.ga4MeasurementId || '').trim();

      try {
        this.i18n = JSON.parse(this.dataset.i18n || '{}');
      } catch (_) {
        this.i18n = {};
      }

      this.handleClick = this.handleClick.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleConsentCollected = this.handleConsentCollected.bind(this);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('input', this.handleInput);
      document.addEventListener('visitorConsentCollected', this.handleConsentCollected);
      document.addEventListener('trackingConsentAccepted', this.handleConsentCollected);
      this.loadCustomerPrivacyApi();

      this.discoverInitialFacets();
      this.render();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('input', this.handleInput);
      document.removeEventListener('visitorConsentCollected', this.handleConsentCollected);
      document.removeEventListener('trackingConsentAccepted', this.handleConsentCollected);
      if (this.analyticsRetryTimer) clearTimeout(this.analyticsRetryTimer);
      this.analyticsRetryTimer = null;
      this.analyticsRetryAttempts = 0;
      this.pendingAnalyticsPayloads = [];
    }

    splitList(raw) {
      return (raw || '').split(',').map((s) => s.trim()).filter(Boolean);
    }

    t(key, fallback) {
      return this.i18n[key] || fallback || '';
    }

    escapeHtml(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));
    }

    getPreviewParams() {
      const current = new URLSearchParams(window.location.search);
      const passthrough = ['preview_theme_id', '_fd', '_ab', '_sc', 'pb'];
      const out = [];
      for (const key of passthrough) {
        const val = current.get(key);
        if (val !== null) out.push(`${encodeURIComponent(key)}=${encodeURIComponent(val)}`);
      }
      return out.join('&');
    }

    async fetchBasePage() {
      // Plain abort() -> rejects with a DOMException named 'AbortError'.
      // Passing a string reason would reject with that string, so `err.name`
      // would be undefined and the catch blocks would show an empty error.
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      const previewParams = this.getPreviewParams();
      const sep = this.baseCollectionUrl.includes('?') ? '&' : '?';
      const url = previewParams
        ? `${this.baseCollectionUrl}${sep}${previewParams}`
        : this.baseCollectionUrl;

      const response = await fetch(url, { method: 'GET', signal: this.abortController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status} — ${url}`);
      return response.text();
    }

    async fetchSectionResponse(filterParams) {
      // Plain abort() -> rejects with a DOMException named 'AbortError'.
      // Passing a string reason would reject with that string, so `err.name`
      // would be undefined and the catch blocks would show an empty error.
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      const sep = this.baseCollectionUrl.includes('?') ? '&' : '?';
      let url = `${this.baseCollectionUrl}${sep}section_id=${encodeURIComponent(this.sectionId)}`;
      if (filterParams) url += `&${filterParams}`;
      const previewParams = this.getPreviewParams();
      if (previewParams) url += `&${previewParams}`;

      const response = await fetch(url, { method: 'GET', signal: this.abortController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status} — ${url}`);
      return response.text();
    }

    discoverSectionId(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const facetsForm = doc.querySelector('form#facets[data-filter-section-id]');
      if (facetsForm) return facetsForm.dataset.filterSectionId;
      const sectionWrap = [...doc.querySelectorAll('[id^="shopify-section-"]')]
        .find((el) => el.querySelector('input[name^="filter."]'));
      if (sectionWrap) return sectionWrap.id.replace(/^shopify-section-/, '');
      return null;
    }

    parseFacets(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const filterDetails = doc.querySelectorAll('.facets__filters details.filter');

      return [...filterDetails].map((details) => {
        const labelEl = details.querySelector('summary .flex-auto.font-bold');
        const label = labelEl ? labelEl.textContent.trim() : '';
        const type = details.dataset.type;
        if (!label || type === 'price_range') return null;

        const inputs = details.querySelectorAll('input[type="checkbox"][name^="filter."]');
        if (inputs.length === 0) return null;

        const values = [...inputs].map((input) => {
          const lbl = input.nextElementSibling;
          const valueLabel = lbl && lbl.querySelector('.flex-auto')
            ? lbl.querySelector('.flex-auto').textContent.trim()
            : input.value;
          const countEl = lbl ? lbl.querySelector('.filter__count') : null;
          const countMatch = countEl ? countEl.textContent.match(/(\d+)/) : null;
          const count = countMatch ? parseInt(countMatch[1], 10) : 0;

          return {
            value: input.value,
            label: valueLabel,
            paramName: input.name,
            count,
            disabled: input.disabled
          };
        }).filter((v) => !v.disabled && v.count > 0);

        if (values.length === 0) return null;
        return { label, type, paramName: values[0].paramName, values };
      }).filter(Boolean);
    }

    parseResultsHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.getElementById('filter-results');
      return el ? el.innerHTML : '';
    }

    findFacetByLabel(label) {
      if (!this.state.facets || !label) return null;
      return this.state.facets.find(
        (f) => f.label.toLowerCase() === label.toLowerCase()
      );
    }

    async discoverInitialFacets() {
      try {
        this.setLoading(true);
        const html = await this.fetchBasePage();

        const discoveredId = this.discoverSectionId(html);
        if (discoveredId) this.sectionId = discoveredId;

        const facets = this.parseFacets(html);
        this.state.facets = facets;

        const sizeFilter = facets.find(
          (f) => f.label.toLowerCase() === this.sizeFilterLabel.toLowerCase()
        );
        if (sizeFilter) {
          this.state.sizeFilterParam = sizeFilter.paramName;
          this.state.sizeAvailableValues = sizeFilter.values.map((v) => v.value);
        } else {
          this.state.error = this.t(
            'error_no_size_filter',
            'No size filter found on collection page.'
          );
        }
        this.setLoading(false);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.state.error = err.message || this.t('error_generic', 'Something went wrong.');
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      this.state.loading = loading;
      this.render();
    }

    /**
     * Keys of picks that are actually applied as filters.
     * Invariant: a pick stored under key k was made while step === k, so it only
     * counts once we have moved past it. A pick stored under the *current* step
     * exists only when the shopper stepped back onto that question; it is shown
     * as pre-selected but must not filter anything until re-confirmed.
     */
    appliedPickKeys() {
      return Object.keys(this.state.picks)
        .map(Number)
        .filter((k) => k < this.state.step)
        .sort((a, b) => a - b);
    }

    buildFilterParams() {
      const params = new URLSearchParams();
      if (this.state.size && this.state.sizeFilterParam) {
        params.append(this.state.sizeFilterParam, this.state.size);
      }
      for (const k of this.appliedPickKeys()) {
        const pick = this.state.picks[k];
        if (pick) params.append(pick.paramName, pick.value);
      }
      return params.toString();
    }

    buildCollectionUrl() {
      const sep = this.baseCollectionUrl.includes('?') ? '&' : '?';
      const params = this.buildFilterParams();
      return params ? `${this.baseCollectionUrl}${sep}${params}` : this.baseCollectionUrl;
    }

    async navigateToStep(targetStep) {
      this.state.step = targetStep;
      this.state.resultsHtml = null;
      this.state.error = null;
      this.state.viewingValues = false;
      this.state.searchQuery = '';

      const keysToClear = Object.keys(this.state.picks).map(Number).filter((k) => k >= targetStep);
      for (const k of keysToClear) delete this.state.picks[k];

      if (targetStep === 0) {
        this.state.size = null;
        return this.render();
      }

      this.state.loading = true;
      this.render();

      try {
        const html = await this.fetchSectionResponse(this.buildFilterParams());

        if (targetStep > this.drillAxes.length) {
          this.state.resultsHtml = this.parseResultsHtml(html);
          this.state.loading = false;
          this.render();
          if (typeof initLazyImages === 'function') initLazyImages();
          document.dispatchEvent(new CustomEvent('on:shirt-finder:results-rendered', { bubbles: true }));
          this.trackResults();
          return;
        }

        this.state.facets = this.parseFacets(html);

        const currentAxisLabel = this.drillAxes[targetStep - 1];
        const axisData = this.findFacetByLabel(currentAxisLabel);
        if (!axisData || axisData.values.length === 0) {
          console.warn(`[shirt-finder] auto-skipping step ${targetStep} ('${currentAxisLabel}') — geen waarden`);
          this.state.loading = false;
          return this.navigateToStep(targetStep + 1);
        }

        const isOptional = targetStep >= this.optionalFromIndex + 1;

        if (axisData.values.length === 1) {
          const only = axisData.values[0];
          if (!isOptional) {
            this.state.picks[targetStep] = {
              paramName: only.paramName,
              value: only.value,
              label: only.label,
              axisLabel: currentAxisLabel
            };
          }
          console.warn(`[shirt-finder] auto-advancing step ${targetStep} ('${currentAxisLabel}') — 1 waarde (${only.label})`);
          this.state.loading = false;
          return this.navigateToStep(targetStep + 1);
        }

        if (isOptional && axisData.values.length <= this.autoSkipThreshold) {
          console.warn(`[shirt-finder] auto-skipping optional step ${targetStep} ('${currentAxisLabel}') — ${axisData.values.length} waarden (drempel ${this.autoSkipThreshold})`);
          this.state.loading = false;
          return this.navigateToStep(targetStep + 1);
        }

        this.state.loading = false;
        this.render();
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.state.error = err.message;
        this.state.loading = false;
        this.render();
      }
    }

    // ---------- HISTORY ----------

    /**
     * Snapshot the screen the shopper is looking at right now.
     * Called just before every forward move, so the stack only ever contains
     * screens that were actually shown. Steps that navigateToStep() auto-skips
     * or auto-advances never enter the stack, which is what makes 'Vorige'
     * land on the previous *question* instead of bouncing forward again.
     */
    pushHistory() {
      this.state.history.push({
        step: this.state.step,
        size: this.state.size,
        kids: this.state.kids,
        picks: Object.assign({}, this.state.picks),
        facets: this.state.facets,
        viewingValues: this.state.viewingValues,
        searchQuery: this.state.searchQuery
      });
    }

    restoreSnapshot(snapshot) {
      this.state.step = snapshot.step;
      this.state.size = snapshot.size;
      this.state.kids = snapshot.kids;
      this.state.picks = Object.assign({}, snapshot.picks);
      this.state.facets = snapshot.facets;
      this.state.viewingValues = snapshot.viewingValues;
      this.state.searchQuery = snapshot.searchQuery;
      this.state.resultsHtml = null;
      this.state.error = null;
      this.state.loading = false;
    }

    canGoBack() {
      return this.state.history.length > 0;
    }

    // ---------- ANALYTICS ----------

    analyticsKeyForAxis(axisLabel, index) {
      const key = String(axisLabel || '').trim().toLowerCase();
      const knownKeys = {
        competitie: 'competition',
        land: 'country',
        club: 'club',
        speler: 'player',
        seizoen: 'season',
        tijdperk: 'era'
      };
      return knownKeys[key] || `filter_${index + 1}`;
    }

    buildAnalyticsPayload() {
      const appliedKeys = this.appliedPickKeys();
      const searchParts = [];
      const payload = {
        send_to: this.ga4MeasurementId,
        search_term: '',
        shirt_finder_size: this.state.size || '',
        shirt_finder_filter_count: appliedKeys.length
      };

      if (this.state.size) searchParts.push(`${this.sizeFilterLabel}: ${this.state.size}`);

      appliedKeys.forEach((pickKey, index) => {
        const pick = this.state.picks[pickKey];
        if (!pick) return;
        const analyticsKey = this.analyticsKeyForAxis(pick.axisLabel, index);
        payload[`shirt_finder_${analyticsKey}`] = pick.label;
        searchParts.push(`${pick.axisLabel}: ${pick.label}`);
      });

      payload.search_term = searchParts.join(' | ').slice(0, 100);
      return payload;
    }

    trackResults() {
      if (!this.ga4MeasurementId) return;
      try {
        if (this.analyticsConsentState() === 'denied') return;
        this.pendingAnalyticsPayloads.push(this.buildAnalyticsPayload());
        if (this.analyticsRetryAttempts >= 40) this.analyticsRetryAttempts = 0;
        this.processPendingAnalytics();
      } catch (err) {
        console.warn('[shirt-finder] analytics kon niet worden klaargezet', err);
      }
    }

    analyticsConsentState() {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy) return 'unknown';

      try {
        if (typeof privacy.currentVisitorConsent === 'function') {
          const consent = privacy.currentVisitorConsent();
          if (consent && consent.analytics === 'no') return 'denied';
        }
        if (typeof privacy.analyticsProcessingAllowed === 'function'
          && privacy.analyticsProcessingAllowed() === true) {
          return 'allowed';
        }
      } catch (_) {
        return 'unknown';
      }
      return 'unknown';
    }

    clearAnalyticsRetry() {
      if (this.analyticsRetryTimer) clearTimeout(this.analyticsRetryTimer);
      this.analyticsRetryTimer = null;
      this.analyticsRetryAttempts = 0;
    }

    processPendingAnalytics() {
      const consentState = this.analyticsConsentState();
      if (consentState === 'denied') {
        this.pendingAnalyticsPayloads = [];
        this.clearAnalyticsRetry();
        return;
      }
      if (consentState !== 'allowed') return;
      if (this.flushPendingAnalytics()) {
        this.clearAnalyticsRetry();
      } else {
        this.scheduleAnalyticsFlush();
      }
    }

    sendAnalyticsPayload(payload) {
      if (typeof window.gtag === 'function') {
        try {
          window.gtag('event', 'search', payload);
          return true;
        } catch (_) {
          // Fall through to the standard Google data layer queue.
        }
      }

      if (!Array.isArray(window.dataLayer)) return false;
      (function queueGtagCommand() {
        window.dataLayer.push(arguments);
      }('event', 'search', payload));
      return true;
    }

    flushPendingAnalytics() {
      if (this.pendingAnalyticsPayloads.length === 0
        || this.analyticsConsentState() !== 'allowed') {
        return false;
      }

      while (this.pendingAnalyticsPayloads.length > 0) {
        if (!this.sendAnalyticsPayload(this.pendingAnalyticsPayloads[0])) return false;
        this.pendingAnalyticsPayloads.shift();
      }
      return true;
    }

    scheduleAnalyticsFlush() {
      if (this.pendingAnalyticsPayloads.length === 0
        || this.analyticsRetryTimer
        || this.analyticsConsentState() !== 'allowed'
        || this.analyticsRetryAttempts >= 40) {
        return;
      }
      this.analyticsRetryAttempts += 1;
      this.analyticsRetryTimer = setTimeout(() => {
        this.analyticsRetryTimer = null;
        this.processPendingAnalytics();
      }, 250);
    }

    handleConsentCollected(evt) {
      const analyticsAllowed = evt.detail && evt.detail.analyticsAllowed;
      if (analyticsAllowed === false) {
        this.pendingAnalyticsPayloads = [];
        this.clearAnalyticsRetry();
        return;
      }
      this.analyticsRetryAttempts = 0;
      this.processPendingAnalytics();
    }

    loadCustomerPrivacyApi() {
      const shopify = window.Shopify;
      if (!shopify || typeof shopify.loadFeatures !== 'function') return;
      if (shopify.customerPrivacy
        && typeof shopify.customerPrivacy.analyticsProcessingAllowed === 'function') {
        return;
      }

      shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (!error) this.processPendingAnalytics();
        }
      );
    }

    // ---------- ACTIONS ----------

    async pickSize(size) {
      this.state.size = size;
      this.state.picks = {};
      this.pushHistory();
      await this.navigateToStep(1);
    }

    async pickAxisValue(axisLabel, paramName, value, label) {
      this.state.picks[this.state.step] = { paramName, value, label, axisLabel };
      this.pushHistory();
      await this.navigateToStep(this.state.step + 1);
    }

    async skipToResults() {
      this.pushHistory();
      this.clearUnconfirmedPicks();
      await this.navigateToStep(this.drillAxes.length + 1);
    }

    async skipStep() {
      this.pushHistory();
      this.clearUnconfirmedPicks();
      await this.navigateToStep(this.state.step + 1);
    }

    /** Drop a pre-selected choice on the current step that was never re-confirmed. */
    clearUnconfirmedPicks() {
      for (const k of Object.keys(this.state.picks).map(Number)) {
        if (k >= this.state.step) delete this.state.picks[k];
      }
    }

    showValues() {
      this.pushHistory();
      this.state.viewingValues = true;
      this.state.searchQuery = '';
      this.render();
    }

    back() {
      if (!this.canGoBack()) return;
      // Drop any in-flight request so a late response cannot overwrite the
      // restored screen.
      if (this.abortController) this.abortController.abort();
      this.restoreSnapshot(this.state.history.pop());
      this.render();
    }

    reset() {
      this.state.step = 0;
      this.state.size = null;
      this.state.picks = {};
      this.state.resultsHtml = null;
      this.state.error = null;
      this.state.viewingValues = false;
      this.state.searchQuery = '';
      this.state.history = [];
      this.render();
    }

    toggleKids(enabled) {
      this.state.kids = enabled;
      this.render();
    }

    handleClick(evt) {
      const target = evt.target.closest('[data-action]');
      if (!target || !this.contains(target)) return;
      const action = target.dataset.action;

      switch (action) {
        case 'pick-size':
          evt.preventDefault();
          this.pickSize(target.dataset.value);
          break;
        case 'pick-value':
          evt.preventDefault();
          this.pickAxisValue(
            target.dataset.axis,
            target.dataset.paramName,
            target.dataset.value,
            target.dataset.label
          );
          break;
        case 'show-values':
          evt.preventDefault();
          this.showValues();
          break;
        case 'skip-step':
          evt.preventDefault();
          this.skipStep();
          break;
        case 'show-results':
          evt.preventDefault();
          this.skipToResults();
          break;
        case 'toggle-kids':
          evt.preventDefault();
          this.toggleKids(target.dataset.kids === 'true');
          break;
        case 'back':
          evt.preventDefault();
          this.back();
          break;
        case 'reset':
          evt.preventDefault();
          this.reset();
          break;
        case 'retry':
          evt.preventDefault();
          this.state.error = null;
          if (this.state.step === 0 && !this.state.facets) {
            this.discoverInitialFacets();
          } else {
            this.navigateToStep(this.state.step);
          }
          break;
        default:
          break;
      }
    }

    handleInput(evt) {
      const target = evt.target;
      if (target && target.dataset && target.dataset.action === 'search') {
        this.state.searchQuery = target.value || '';
        this.render();
      }
    }

    // ---------- RENDER ----------

    render() {
      const activeSearch = this.querySelector('input[data-action="search"]');
      const wasSearching = activeSearch && document.activeElement === activeSearch;
      const selStart = wasSearching ? activeSearch.selectionStart : null;
      const selEnd = wasSearching ? activeSearch.selectionEnd : null;

      this.innerHTML = this.renderShell();

      if (wasSearching) {
        const newSearch = this.querySelector('input[data-action="search"]');
        if (newSearch) {
          newSearch.focus({ preventScroll: true });
          if (selStart !== null) {
            try { newSearch.setSelectionRange(selStart, selEnd); } catch (_) { /* noop */ }
          }
        }
      } else {
        const heading = this.querySelector('.shirt-finder__heading');
        if (heading) heading.focus({ preventScroll: true });
      }
    }

    renderShell() {
      if (this.state.error) return this.renderError();
      if (this.state.loading && this.state.step === 0 && !this.state.facets) {
        return this.renderInitialLoading();
      }
      if (this.state.step === 0) return this.renderStepSize();
      if (this.state.step > this.drillAxes.length) return this.renderStepResults();
      return this.renderStepDrill();
    }

    renderProgress(currentStep) {
      const totalSteps = this.drillAxes.length + 1;
      const dots = [];
      for (let i = 0; i < totalSteps; i++) {
        const cls = i < currentStep ? 'shirt-finder__progress-dot is-done'
          : i === currentStep ? 'shirt-finder__progress-dot is-current'
            : 'shirt-finder__progress-dot';
        dots.push(`<span class="${cls}" aria-hidden="true"></span>`);
      }
      return `<div class="shirt-finder__progress" role="progressbar" aria-valuenow="${currentStep + 1}" aria-valuemin="1" aria-valuemax="${totalSteps}">${dots.join('')}</div>`;
    }

    renderBackButton() {
      if (!this.canGoBack()) return '';
      return `<button type="button" class="shirt-finder__back link text-current text-sm" data-action="back">
        <span aria-hidden="true">&larr;</span> ${this.escapeHtml(this.t('back', 'Vorige'))}
      </button>`;
    }

    renderInitialLoading() {
      return `<div class="shirt-finder__loading" aria-live="polite" aria-busy="true">
        <span class="shirt-finder__spinner" aria-hidden="true"></span>
        <p>${this.escapeHtml(this.t('loading', 'Laden...'))}</p>
      </div>`;
    }

    renderError() {
      return `<div class="shirt-finder__error" role="alert">
        <p class="font-bold">${this.escapeHtml(this.t('error_title', 'Er ging iets mis'))}</p>
        <p>${this.escapeHtml(this.state.error || '')}</p>
        <button type="button" class="btn btn--secondary" data-action="retry">
          ${this.escapeHtml(this.t('error_retry', 'Opnieuw proberen'))}
        </button>
        ${this.renderBackButton()}
      </div>`;
    }

    renderPicksSummary() {
      const parts = [];
      if (this.state.size) parts.push(`${this.sizeFilterLabel} ${this.state.size}`);
      for (const k of this.appliedPickKeys()) {
        const pick = this.state.picks[k];
        if (pick) parts.push(`${pick.axisLabel}: ${pick.label}`);
      }
      if (parts.length === 0) return '';
      const tpl = this.t('step_drill_subhead', 'Tot nu toe: __picks__');
      const txt = tpl.replace('__picks__', parts.join(' · '));
      return `<p class="shirt-finder__subhead">${this.escapeHtml(txt)}</p>`;
    }

    renderShowResultsButton() {
      if (this.state.step < this.optionalFromIndex + 1) return '';
      return `<div class="shirt-finder__results-cta">
        <button type="button" class="btn btn--secondary" data-action="show-results">
          ${this.escapeHtml(this.t('show_results_now', 'Toon resultaten'))}
        </button>
      </div>`;
    }

    renderStepSize() {
      const sizes = this.state.kids ? this.kidsSizes : this.adultSizes;
      const available = new Set(this.state.sizeAvailableValues || []);

      const buttons = sizes.map((size) => {
        const isAvailable = available.size === 0 || available.has(size);
        const isSelected = this.state.size === size;
        const disabledAttr = isAvailable ? '' : 'disabled aria-disabled="true"';
        let cls = 'shirt-finder__choice';
        if (!isAvailable) cls += ' is-unavailable';
        if (isSelected) cls += ' is-selected';
        return `<button type="button" class="${cls}" data-action="pick-size" data-value="${this.escapeHtml(size)}" aria-pressed="${isSelected}" ${disabledAttr}>
          <span class="shirt-finder__choice-label">${this.escapeHtml(size)}</span>
        </button>`;
      }).join('');

      const toggle = this.kidsToggleEnabled ? `
        <div class="shirt-finder__toggle" role="tablist" aria-label="${this.escapeHtml(this.t('size_group', 'Maatgroep'))}">
          <button type="button" role="tab" aria-selected="${!this.state.kids}" class="shirt-finder__toggle-btn ${!this.state.kids ? 'is-active' : ''}" data-action="toggle-kids" data-kids="false">
            ${this.escapeHtml(this.t('adult_sizes', 'Volwassen'))}
          </button>
          <button type="button" role="tab" aria-selected="${this.state.kids}" class="shirt-finder__toggle-btn ${this.state.kids ? 'is-active' : ''}" data-action="toggle-kids" data-kids="true">
            ${this.escapeHtml(this.t('kids_sizes', 'Kindermaten'))}
          </button>
        </div>
      ` : '';

      return `${this.renderProgress(0)}
        <div class="shirt-finder__step">
          <h3 tabindex="-1" class="shirt-finder__heading h4">${this.escapeHtml(this.t('step_size_title', 'Welke maat?'))}</h3>
          ${toggle}
          <div class="shirt-finder__choices shirt-finder__choices--sizes">${buttons}</div>
        </div>`;
    }

    isOptionalStep() {
      return this.state.step >= this.optionalFromIndex + 1;
    }

    renderStepDrill() {
      if (this.state.loading) return this.renderInitialLoading();

      const axisLabel = this.drillAxes[this.state.step - 1];
      if (!axisLabel) return this.renderStepResults();

      const axis = this.findFacetByLabel(axisLabel);
      if (!axis || axis.values.length === 0) {
        return `${this.renderProgress(this.state.step)}
          ${this.renderBackButton()}
          <div class="shirt-finder__step">
            <h3 tabindex="-1" class="shirt-finder__heading h4">${this.escapeHtml(axisLabel)}</h3>
            <p>${this.escapeHtml(this.t('no_axes_available', 'Geen opties. Klik door of ga terug.'))}</p>
            ${this.renderShowResultsButton()}
          </div>`;
      }

      if (this.isOptionalStep() && !this.state.viewingValues) {
        return this.renderStepDrillQuestion(axisLabel);
      }

      return this.renderStepDrillValues(axisLabel, axis);
    }

    renderStepDrillQuestion(axisLabel) {
      const axisLower = axisLabel.toLowerCase();
      const questionTpl = this.t('step_drill_question', 'Wil je een specifieke __axis__ kiezen?');
      const yesTpl = this.t('step_drill_yes', 'Ja, kies __axis__');
      const noText = this.t('step_drill_no', 'Nee, sla over');

      return `${this.renderProgress(this.state.step)}
        ${this.renderBackButton()}
        <div class="shirt-finder__step shirt-finder__step--question">
          <h3 tabindex="-1" class="shirt-finder__heading h4">${this.escapeHtml(questionTpl.replace('__axis__', axisLower))}</h3>
          ${this.renderPicksSummary()}
          <div class="shirt-finder__question-actions">
            <button type="button" class="btn btn--primary" data-action="show-values">
              ${this.escapeHtml(yesTpl.replace('__axis__', axisLower))}
            </button>
            <button type="button" class="btn btn--secondary" data-action="skip-step">
              ${this.escapeHtml(noText)}
            </button>
          </div>
          <div class="shirt-finder__question-link">
            <button type="button" class="link text-current text-sm" data-action="show-results">
              ${this.escapeHtml(this.t('show_results_now', 'Toon resultaten nu'))} &rarr;
            </button>
          </div>
        </div>`;
    }

    renderStepDrillValues(axisLabel, axis) {
      const query = (this.state.searchQuery || '').trim().toLowerCase();
      const sorted = axis.values.slice().sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
      const filtered = query
        ? sorted.filter((v) => v.label.toLowerCase().includes(query))
        : sorted;

      const currentPick = this.state.picks[this.state.step];
      const buttons = filtered.map((v) => {
        const isSelected = !!currentPick && currentPick.value === v.value;
        const cls = isSelected
          ? 'shirt-finder__choice shirt-finder__choice--value is-selected'
          : 'shirt-finder__choice shirt-finder__choice--value';
        return `<button type="button" class="${cls}" data-action="pick-value" data-axis="${this.escapeHtml(axisLabel)}" data-param-name="${this.escapeHtml(v.paramName)}" data-value="${this.escapeHtml(v.value)}" data-label="${this.escapeHtml(v.label)}" aria-pressed="${isSelected}">
          <span class="shirt-finder__choice-label">${this.escapeHtml(v.label)}</span>
          <span class="shirt-finder__choice-count">${v.count}</span>
        </button>`;
      }).join('');

      const titleTpl = this.t('step_drill_title', 'Kies een __axis__');
      const title = titleTpl.replace('__axis__', axisLabel.toLowerCase());

      const showSearch = axis.values.length >= this.searchThreshold;
      const searchHtml = showSearch
        ? `<div class="shirt-finder__search">
            <input type="search"
                   data-action="search"
                   class="shirt-finder__search-input"
                   placeholder="${this.escapeHtml(this.t('search_placeholder', 'Zoek...'))}"
                   value="${this.escapeHtml(this.state.searchQuery)}"
                   aria-label="${this.escapeHtml(this.t('search_label', 'Zoek opties'))}"
                   autocomplete="off"
                   enterkeyhint="search">
          </div>`
        : '';

      const emptyHtml = filtered.length === 0
        ? `<p class="shirt-finder__empty-search">${this.escapeHtml(this.t('search_no_results', 'Geen treffers voor je zoekopdracht.'))}</p>`
        : '';

      return `${this.renderProgress(this.state.step)}
        ${this.renderBackButton()}
        <div class="shirt-finder__step">
          <h3 tabindex="-1" class="shirt-finder__heading h4">${this.escapeHtml(title)}</h3>
          ${this.renderPicksSummary()}
          ${searchHtml}
          ${emptyHtml}
          <div class="shirt-finder__choices shirt-finder__choices--values">${buttons}</div>
          ${this.renderShowResultsButton()}
        </div>`;
    }

    renderStepResults() {
      const fullUrl = this.buildCollectionUrl();
      const parts = [];
      if (this.state.size) parts.push(`${this.sizeFilterLabel} ${this.state.size}`);
      for (const k of this.appliedPickKeys()) {
        const pick = this.state.picks[k];
        if (pick) parts.push(`${pick.axisLabel}: ${pick.label}`);
      }
      const summary = parts.join(' · ');

      let body = '';
      if (this.state.loading) {
        body = this.renderInitialLoading();
      } else if (!this.state.resultsHtml) {
        body = `<p class="shirt-finder__empty">${this.escapeHtml(this.t('no_results', 'Geen treffers. Probeer een andere combinatie.'))}</p>
          <button type="button" class="btn btn--secondary" data-action="reset">
            ${this.escapeHtml(this.t('restart', 'Opnieuw beginnen'))}
          </button>`;
      } else {
        body = `<div class="shirt-finder__results-grid">${this.state.resultsHtml}</div>
          <div class="shirt-finder__results-actions">
            <a href="${this.escapeHtml(fullUrl)}" class="btn btn--primary">
              ${this.escapeHtml(this.t('view_all_results', 'Bekijk alle resultaten'))}
            </a>
            <button type="button" class="btn btn--secondary" data-action="reset">
              ${this.escapeHtml(this.t('restart', 'Opnieuw beginnen'))}
            </button>
          </div>`;
      }

      return `<div class="shirt-finder__results">
          <div class="shirt-finder__results-header">
            <h3 tabindex="-1" class="shirt-finder__heading h4">${this.escapeHtml(this.t('results_title', 'Jouw matches'))}</h3>
            <p class="shirt-finder__summary">${this.escapeHtml(summary)}</p>
            ${this.renderBackButton()}
          </div>
          ${body}
        </div>`;
    }
  }

  customElements.define('shirt-finder-quiz', ShirtFinderQuiz);
}
